import { Component, Input, forwardRef } from "@angular/core";
import { MedicalOrderOperation } from "../../../model/medical-order-operation";
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormBuilder,
  FormGroup,
  Validators,
} from "@angular/forms";
import { PdfService } from "../../../services/pdf.service";
import { AdmissionService } from "../../../services/admission.service";
import { NotificationToastService } from "../../../services/notification-toast.service";
import { ServiceService } from "../../../services/service.service";
import { Service } from "../../../model/service";
import { MatDialog, MatIconRegistry } from "@angular/material";
import { DomSanitizer } from "@angular/platform-browser";
import { AuthService } from "src/app/services/auth.service";
import { ReservationService } from "src/app/services/reservation.service";
import { PatientMailEditDialogComponent } from "../../dialogs/patient-mail-edit-dialog/patient-mail-edit-dialog.component";
import { PatientService } from "src/app/services/patient.service";
import { StudyReportService } from "src/app/services/study-report/study-report.service";

export const MEDICAL_ORDER_OPERATION_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => MedicalOrderOperationControlComponent),
  multi: true,
};

@Component({
  selector: "app-medical-order-operation-control-component",
  providers: [MEDICAL_ORDER_OPERATION_CONTROL_VALUE_ACCESSOR],
  templateUrl: "./medical-order-operation-control.component.html",
  styleUrls: ["./medical-order-operation-control.component.css"],
})
export class MedicalOrderOperationControlComponent
  implements ControlValueAccessor
{
  form: FormGroup;
  onChange;
  loading = false;

  printQuestionnaire = false;
  printConsent = false;
  printTicket = false;
  printMedicalOrderTicket = false;
  canSendOrderWhatsapp = false;
  medicalAppointmentIds: number[];

  constructor(
    private formBuilder: FormBuilder,
    private admissionService: AdmissionService,
    private authService: AuthService,
    private studyReportService: StudyReportService,
    private serviceService: ServiceService,
    private notificationToastService: NotificationToastService,
    private pdfService: PdfService,
    private matIconRegistry: MatIconRegistry,
    private patientService: PatientService,
    private reservationService: ReservationService,
    private dialog: MatDialog,
    sanitizer: DomSanitizer
  ) {
    this.canSendOrderWhatsapp = this.authService.canSendOrderWhatsapp();
    this.matIconRegistry.addSvgIcon(
      "ws",
      sanitizer.bypassSecurityTrustResourceUrl(
        "assets/images/whatsapp_icon.svg"
      )
    );
    this.form = this.formBuilder.group({
      Protocol: [null],
      MedicalApponitmentId: [null],
      MedicalApponitmentIds: [[]],
      MedicalOrderId: [null],
      HasPreviousStudies: [null],
      ApplyPreviousStudies: [null],
      TicketQuantity: [1, Validators.required],
      MedicalOrderTicketQuantity: [1, Validators.required],
      QuestionnaireQuantity: [1, Validators.required],
      ConsentQuantity: [1, Validators.required],
      DeliveryDate: [  null ],
    });

    this.form.valueChanges.subscribe(() => {
      const medicalOrderOperation = <MedicalOrderOperation>(
        this.form.getRawValue()
      );
      if (this.onChange) {
        this.onChange(medicalOrderOperation);
      }
    });
  }

  get protocol(): string {
    return this.form ? this.form.getRawValue().Protocol : "";
  }

  async refreshDeliveryDate() {
    try {
      if (this.medicalAppointmentIds) {
        for (
          let index = 0;
          index < this.medicalAppointmentIds.length;
          index++
        ) {
          const date = await this.studyReportService.getStudyReportDeliveryDate(
            this.medicalAppointmentIds[index]
          );

          if (date) {

            this.form.patchValue({
              DeliveryDate: date,
            });
          } else {
            this.form.patchValue({
              DeliveryDate: null,
            });
          }
        }
      }
    } catch (err) {
      this.form.patchValue({
        DeliveryDate: JSON.stringify(err.error),
      });

      this.notificationToastService.error(
        JSON.stringify(err.error),
        "Operaciones"
      );
    }
  }

  writeValue(obj: MedicalOrderOperation): void {
    if (obj) {
      if (obj.MedicalApponitmentId && !obj.MedicalApponitmentIds) {
        obj.MedicalApponitmentIds = [obj.MedicalApponitmentId];
      }

      this.medicalAppointmentIds = obj.MedicalApponitmentIds;

      this.refreshDeliveryDate();

      this.form.patchValue({
        Protocol: obj.Protocol,
        MedicalApponitmentId: obj.MedicalApponitmentId,
        MedicalOrderId: obj.MedicalOrderId,
        HasPreviousStudies: obj.HasPreviousStudies,
        ApplyPreviousStudies: obj.ApplyPreviousStudies,
        MedicalApponitmentIds: obj.MedicalApponitmentIds,
      });

      this.serviceService
        .getServiceByMedicalAppointmentId(obj.MedicalApponitmentId)
        .then((service: Service) => {
          this.printConsent = service.PrintConsent;
          this.printQuestionnaire = service.PrintQuestionnaire;
          this.printMedicalOrderTicket = service.PrintMedicalOrderTicket;
          this.printTicket = service.PrintTicket;
          this.form.patchValue({
            TicketQuantity: service.TicketQuantity,
            MedicalOrderTicketQuantity: service.MedicalOrderTicketQuantity,
          });
        });
    }
  }

  sendMail() {
    this.notificationToastService.success(
      "Se ha enviado el mail",
      "Envio de mails"
    );
    this.reservationService
      .sendResevationMail(this.medicalAppointmentIds)
      .catch((err) =>
        this.notificationToastService.error(
          "Error al enviar el mail " + JSON.stringify(err.error),
          "Envio de mails"
        )
      );
  }

  async printTicketReport() {
    try {
      if (
        this.form.controls.TicketQuantity.value &&
        this.form.controls.MedicalApponitmentId.value
      ) {
        this.loading = true;

        let index = 0;

        if (this.pdfService.directPrintEnabled()) {
          while (index < this.form.controls.TicketQuantity.value) {
            this.pdfService.directPrint(
              "ticket",
              JSON.stringify({
                turnoIds: [this.form.controls.MedicalApponitmentId.value],
              })
            );
            index = index + 1;
          }
        } else {
          const report = await this.admissionService.getTicketReportPdf(
            this.form.controls.MedicalApponitmentId.value
          );
          while (index < this.form.controls.TicketQuantity.value) {
            this.pdfService.base64ToPdfPrint(report.ReportBase64, "ticket");
            index = index + 1;
          }
        }
      }
    } catch (err) {
      this.notificationToastService.error(
        JSON.stringify(err.error),
        "Operaciones"
      );
    } finally {
      setTimeout(() => {
        this.loading = false;
      }, 1000);
    }
  }

  async registerPatientMessage() {
    try {
      if (
        this.form.controls.MedicalOrderTicketQuantity.value &&
        this.form.controls.MedicalApponitmentId.value
      ) {
        this.loading = true;
        const reports = await this.admissionService.registerMessagePatient(
          this.form.controls.MedicalApponitmentId.value
        );
      }
    } catch (err) {
      this.notificationToastService.error(
        JSON.stringify(err.error),
        "Operaciones"
      );
    } finally {
      this.loading = false;
    }
  }

  async printOrderReport() {
    try {
      if (
        this.form.controls.MedicalOrderTicketQuantity.value &&
        this.form.controls.MedicalApponitmentId.value
      ) {
        this.loading = true;

        const ids = this.form.controls.MedicalApponitmentIds.value;

        for (let indexA = 0; indexA < ids.length; indexA++) {
          const reports = await this.admissionService.getOrderReportPdf(
            ids[indexA]
          );
          let index = 0;

          if (reports) {
            while (
              index < this.form.controls.MedicalOrderTicketQuantity.value
            ) {
              reports.forEach((report) => {
                this.pdfService.base64ToPdfPrint(report.ReportBase64, "order");
              });
              index = index + 1;
            }
          }
        }
      }
    } catch (err) {
      console.log(err);
      this.notificationToastService.error(
        JSON.stringify(err.error),
        "Operaciones"
      );
    } finally {
      this.loading = false;
    }
  }

  async sendMailOrderReport() {
    try {
      if (this.form.controls.MedicalApponitmentId.value) {
        const ids = this.form.controls.MedicalApponitmentIds.value;

        const patientMail =
          await this.patientService.getPatientMailByAppointmentId(ids[0]);

        const dialogRef = this.dialog.open(PatientMailEditDialogComponent, {
          width: "50%",
          data: patientMail,
        });

        const result = await dialogRef.afterClosed().toPromise();

        this.loading = true;

        if (result) {
          for (let indexA = 0; indexA < ids.length; indexA++) {
            await this.admissionService.sendMailOrderReportPdf(ids[indexA]);
          }
        }
      }
    } catch (err) {
      console.log(err);
      this.notificationToastService.error(
        JSON.stringify(err.error),
        "Operaciones"
      );
    } finally {
      this.loading = false;
    }
  }

  async printConsentReport() {
    try {
      if (
        this.form.controls.ConsentQuantity.value &&
        this.form.controls.MedicalApponitmentId.value
      ) {
        this.loading = true;
        const report = await this.admissionService.getConsentReportPdf(
          this.form.controls.MedicalApponitmentId.value
        );
        let index = 0;

        if (report) {
          while (index < this.form.controls.ConsentQuantity.value) {
            this.pdfService.base64ToPdfPrint(report.ReportBase64);
            index = index + 1;
          }
        }
      }
    } catch (err) {
      this.notificationToastService.error(
        JSON.stringify(err.error),
        "Operaciones"
      );
    } finally {
      this.loading = false;
    }
  }

  async printQuestionnaireReport() {
    try {
      if (
        this.form.controls.QuestionnaireQuantity.value &&
        this.form.controls.MedicalApponitmentId.value
      ) {
        this.loading = true;
        const report = await this.admissionService.getQuestionnaireReportPdf(
          this.form.controls.MedicalApponitmentId.value
        );
        let index = 0;

        if (report) {
          while (index < this.form.controls.QuestionnaireQuantity.value) {
            this.pdfService.base64ToPdfPrint(report.ReportBase64);
            index = index + 1;
          }
        }
      }
    } catch (err) {
      this.notificationToastService.error(
        JSON.stringify(err.error),
        "Operaciones"
      );
    } finally {
      this.loading = false;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {}

  setDisabledState?(isDisabled: boolean): void {}
}
